import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import SidebarCustomization from './SidebarCustomization';
import { getAuthHeaders } from '../utils/authHeaders';
import { clearSessionDrafts } from '../utils/sessionDrafts';
import { activeNavigation, availableNavigation, orderedNavigation, preferenceKey, readNavigationPreferences, resolveWorkspaceId, saveNavigationPreferences } from '../navigation/workspaceNavigation';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const PIN_KEY = 'access.sidebar.pinned';
const readStorage = key => { try { return localStorage.getItem(key); } catch { return null; } };
const readUser = () => { try { return JSON.parse(readStorage('user') || '{}') || {}; } catch { return {}; } };
const narrowScreen = () => Boolean(window.matchMedia?.('(max-width: 760px)').matches);
const plainTitle = title => title.replace(/^[^\p{L}\p{N}]+/u, '');

function SubmoduleHeader({ group, item, workspaceName, hidden, onShow, onNavigation, pathname }) {
  const items = group?.items || [];
  const title = group ? plainTitle(group.title) : pathname === '/company/notifications' ? 'Notifications' : 'Workspace';
  return <header className="workspace-module-header">
    <div className="workspace-module-heading"><div><p className="workspace-context-label">{workspaceName}</p><h1 className="workspace-module-title">{title}</h1>{hidden && <p className="workspace-hidden-label">This module is hidden from your sidebar.</p>}</div>{hidden && <button type="button" className="workspace-show-module" onClick={onShow}>Show in sidebar</button>}</div>
    {items.length > 1 && <div className="workspace-submodule-row">
      <nav className="workspace-submodule-nav" aria-label={`${title} submodules`}>
        {items.map(([path, label]) => <Link key={path} to={path} aria-current={item?.[0] === path ? 'page' : undefined} onClick={onNavigation}>{label}</Link>)}
      </nav>
    </div>}
  </header>;
}

function WorkspaceNavigation({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = readUser();
  const [storedCompanyId, setStoredCompanyId] = useState(() => readStorage('selectedCompanyId'));
  const workspaceId = resolveWorkspaceId(location.pathname, storedCompanyId);
  const [companies, setCompanies] = useState([]);
  const [workspaceRecord, setWorkspaceRecord] = useState(null);
  const [workspaceError, setWorkspaceError] = useState('');
  const [companiesError, setCompaniesError] = useState('');
  const [retry, setRetry] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isPinned, setIsPinned] = useState(() => !narrowScreen() && readStorage(PIN_KEY) !== 'false');
  const [isHovered, setIsHovered] = useState(false);
  const [customizing, setCustomizing] = useState(false);
  const [preferenceNotice, setPreferenceNotice] = useState('');
  const [preferenceOverrides, setPreferenceOverrides] = useState({});
  const lastVisited = useRef({});
  const isOpen = isPinned || isHovered;
  const workspace = String(workspaceRecord?.id) === workspaceId ? workspaceRecord : null;
  const workspaceName = workspaceId === 'holding' ? 'Access LB Holding' : workspace?.name || companies.find(company => String(company.id) === workspaceId)?.name || 'Company workspace';
  const groups = useMemo(() => availableNavigation({ workspaceId, workspace, role: user.role }), [workspaceId, workspace, user.role]);
  const selected = activeNavigation(groups, location.pathname);
  const prefsKey = preferenceKey(user.id || user.email || user.role || 'session', workspaceId);
  const preferences = useMemo(() => preferenceOverrides[prefsKey] || readNavigationPreferences(prefsKey), [preferenceOverrides, prefsKey]);
  const visibleGroups = orderedNavigation(groups, preferences);
  const allGroups = orderedNavigation(groups, preferences, { includeHidden: true });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setCompaniesError('');
    fetch(`${API}/companies`, { headers: getAuthHeaders(), signal: controller.signal }).then(async response => {
      if (!response.ok) throw new Error('The company list could not be loaded.');
      return response.json();
    }).then(data => { if (active) setCompanies(Array.isArray(data.companies) ? data.companies : []); })
      .catch(error => { if (active) setCompaniesError(error.message); });
    return () => { active = false; controller.abort(); };
  }, [retry]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setWorkspaceRecord(null);
    setWorkspaceError('');
    if (workspaceId !== 'holding') {
      fetch(`${API}/companies/${encodeURIComponent(workspaceId)}`, { headers: getAuthHeaders(), signal: controller.signal }).then(async response => {
        if (!response.ok) throw new Error('Company modules could not be loaded.');
        const record = await response.json();
        if (String(record?.id) !== workspaceId) throw new Error('Company modules could not be loaded.');
        return record;
      }).then(record => { if (active) setWorkspaceRecord(record); })
        .catch(error => { if (active) setWorkspaceError(error.message); });
    }
    return () => { active = false; controller.abort(); };
  }, [workspaceId, retry]);

  useEffect(() => {
    const id = location.pathname.match(/^\/dashboard\/(\d+)(?:\/|$)/)?.[1];
    if (id) {
      setStoredCompanyId(id);
      try { localStorage.setItem('selectedCompanyId', id); } catch { /* Navigation remains usable without preferences. */ }
    }
  }, [location.pathname]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setNotificationCount(0);
    if (workspaceId === 'holding') return () => controller.abort();
    const update = async () => {
      try {
        const response = await fetch(`${API}/companies/${workspaceId}/notifications`, { headers: getAuthHeaders(), signal: controller.signal });
        if (!response.ok) return;
        const data = await response.json();
        const list = data.data || data || [];
        if (active) setNotificationCount(Array.isArray(list) ? list.filter(entry => !entry.read_at).length : 0);
      } catch { /* A notification outage must not block navigation. */ }
    };
    update();
    const interval = setInterval(update, 30000);
    return () => { active = false; controller.abort(); clearInterval(interval); };
  }, [workspaceId]);

  useEffect(() => {
    document.documentElement.style.setProperty('--access-sidebar-space', isOpen ? '270px' : '0px');
    return () => document.documentElement.style.removeProperty('--access-sidebar-space');
  }, [isOpen]);
  useEffect(() => {
    const sync = event => {
      if (event.key === PIN_KEY || event.key === null) { setIsPinned(!narrowScreen() && readStorage(PIN_KEY) !== 'false'); setIsHovered(false); }
      if (event.key === prefsKey || event.key === null) setPreferenceOverrides(current => ({ ...current, [prefsKey]: readNavigationPreferences(prefsKey) }));
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [prefsKey]);
  useEffect(() => {
    setCustomizing(false);
    setPreferenceNotice('');
  }, [workspaceId]);
  useEffect(() => {
    if (selected.group) lastVisited.current[`${workspaceId}:${selected.group.id}`] = location.pathname;
  }, [workspaceId, selected.group, location.pathname]);

  const closeOnMobile = () => { if (narrowScreen()) { setIsPinned(false); setIsHovered(false); } };
  const openPage = (path, group) => {
    closeOnMobile();
    if (group?.items.length === 1) { navigate(path); return; }
    if (group && selected.group?.id === group.id) return;
    const remembered = group && lastVisited.current[`${workspaceId}:${group.id}`];
    navigate(remembered || path);
  };
  const switchWorkspace = value => {
    setStoredCompanyId(value === 'holding' ? null : value);
    try {
      if (value === 'holding') localStorage.removeItem('selectedCompanyId');
      else localStorage.setItem('selectedCompanyId', value);
    } catch { /* Keep the selector responsive if preference storage is unavailable. */ }
    closeOnMobile();
    navigate(value === 'holding' ? '/' : `/dashboard/${value}/overview`);
  };
  const updatePreferences = next => {
    setPreferenceOverrides(current => ({ ...current, [prefsKey]: next }));
    const saved = saveNavigationPreferences(prefsKey, next);
    setPreferenceNotice(saved ? '' : 'Your sidebar is updated for this visit. This browser could not save the arrangement.');
  };
  const logout = () => {
    clearSessionDrafts();
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('selectedCompanyId');
    navigate('/login');
  };
  return <div className="App workspace-shell">
    {isOpen && <button type="button" className="workspace-mobile-backdrop" aria-label="Close navigation" onClick={() => { setIsPinned(false); setIsHovered(false); }} />}
    <Sidebar companies={companies} workspaceId={workspaceId} workspaceName={workspaceName} groups={visibleGroups} activeId={selected.group?.id} notificationCount={notificationCount} isOpen={isOpen} isPinned={isPinned}
      onToggle={() => { const next = !isPinned; setIsPinned(next); setIsHovered(false); try { localStorage.setItem(PIN_KEY, String(next)); } catch { /* The toggle still works. */ } }}
      onPreview={() => { if (!narrowScreen()) setIsHovered(true); }} onLeave={() => setIsHovered(false)}
      onWorkspaceChange={switchWorkspace} onNavigate={openPage} onCustomize={() => setCustomizing(true)} onLogout={logout}
      loading={workspaceId !== 'holding' && !workspace && !workspaceError} error={workspaceError || companiesError} onRetry={() => setRetry(value => value + 1)} />
    <div className="app-page-content workspace-main">
      <SubmoduleHeader group={selected.group} item={selected.item} workspaceName={workspaceName} pathname={location.pathname} onNavigation={closeOnMobile}
        hidden={Boolean(selected.group && !visibleGroups.some(group => group.id === selected.group.id))}
        onShow={() => updatePreferences({ ...preferences, hidden: preferences.hidden.filter(id => id !== selected.group.id) })} />
      {preferenceNotice && <p role="status" className="workspace-preference-notice">{preferenceNotice}</p>}
      {children}
    </div>
    {customizing && <SidebarCustomization groups={allGroups} preferences={preferences} workspaceName={workspaceName} onChange={updatePreferences} onClose={() => setCustomizing(false)} />}
  </div>;
}

export default function WorkspaceLayout({ children, showNavigation = true }) {
  if (!showNavigation) return <div className="App" style={{ display: 'flex', minHeight: '100vh', width: '100%' }}><div className="app-page-content" style={{ flex: 1, backgroundColor: '#f8f9fa', position: 'relative' }}>{children}</div></div>;
  return <WorkspaceNavigation>{children}</WorkspaceNavigation>;
}
